import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import clinicAPI from "../../../services/clinicApi";
import {
  adminTrainerProfilePath,
  isOwnTrainerRecord,
} from "../../../utils/trainerProfilePath";
import { formatTimeRu, formatDateRu, toDateInputValue } from "../../../utils/appointmentLabels";
import { useHubJournalUrl } from "../../../utils/hubPaths";
import L from "../../../constants/baysportLabels";
import s from "../clinic.module.css";
import aa from "../AdminPortal/adminApp.module.css";
import { Button, Field, Input, Select, useToast } from "../../ui";
import SessionDateTimeFields from "./SessionDateTimeFields";
import AttendanceAbsenceSheet from "./AttendanceAbsenceSheet";
import CompetitionRegistrationsPanel from "./CompetitionRegistrationsPanel";
import CompetitionDetailsFields from "./CompetitionDetailsFields";
import SessionSheetAttendanceCard from "./SessionSheetAttendanceCard";
import {
  competitionAssemblyLabel,
  competitionFormFromSession,
  competitionPayloadFromForm,
  competitionVenueLine,
} from "../../../utils/competitionUtils";
import {
  attendanceMarkPayload,
  attendanceMarkState,
  canDragSession,
  rollCallStatsFromAttendances,
  canMarkSessionAttendance,
  canRescheduleToTimes,
  isIndividualSession,
  rescheduleBlockedMessage,
  sessionColor,
  sessionLabel,
  sessionScheduleFromIso,
  sessionScheduleToIso,
  sessionDisplayStatus,
} from "./calendarUtils";

function memberInitials(name) {
  return String(name || "?")
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function resolveBranchName(sess, branches) {
  if (sess?.branch?.name) return sess.branch.name;
  const id = sess?.branch_id || sess?.branch?.id;
  if (id && branches?.length) {
    const hit = branches.find((b) => String(b.id) === String(id));
    if (hit?.name) return hit.name;
  }
  return null;
}

function resolveBranchAddress(sess, branches) {
  if (sess?.branch?.address?.trim()) return sess.branch.address.trim();
  const id = sess?.branch_id || sess?.branch?.id;
  if (id && branches?.length) {
    const hit = branches.find((b) => String(b.id) === String(id));
    if (hit?.address?.trim()) return hit.address.trim();
  }
  return null;
}

function resolveTrainerName(sess, doctors) {
  if (sess?.trainer?.user?.full_name) return sess.trainer.user.full_name;
  const docId = sess?.doctor_id;
  if (docId && doctors?.length) {
    const d = doctors.find((x) => String(x.id) === String(docId));
    if (d) return d.user?.full_name || d.full_name;
  }
  return null;
}

export default function DaySessionPanel({
  sessionId,
  sessionPreview,
  groups,
  branches = [],
  doctors = [],
  onUpdated,
  variant = "aside",
  onPanelActionChange,
  sheetBackRef,
  onDismiss,
  onDockChange,
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const buildJournalUrl = useHubJournalUrl();
  const myTrainerId = useSelector((state) => state.auth.trainerProfile?.id);
  const isDialog = variant === "dialog";
  const isSheet = variant === "sheet" || isDialog;
  const [journal, setJournal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [markingId, setMarkingId] = useState(null);
  const [panelAction, setPanelAction] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState(null);
  const [detailsForm, setDetailsForm] = useState(null);
  const [competitionForm, setCompetitionForm] = useState(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const [notePatientId, setNotePatientId] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [absencePick, setAbsencePick] = useState(null);

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const data = await clinicAPI.getSessionJournal(sessionId);
      setJournal(data.session);
      const branchId = data.session.branch_id || data.session.branch?.id || "";
      const doctorId = data.session.doctor_id || data.session.trainer?.id || "";
      setRescheduleForm({
        ...sessionScheduleFromIso(
          data.session.start_datetime,
          data.session.end_datetime,
        ),
        branch_id: branchId,
      });
      setDetailsForm({
        branch_id: branchId,
        doctor_id: doctorId,
      });
      setCompetitionForm(competitionFormFromSession(data.session));
    } catch (e) {
      if (!sessionPreview) {
        toast.error(e.response?.data?.error || "Ошибка загрузки");
      }
      setJournal((prev) => prev || sessionPreview || null);
    } finally {
      setLoading(false);
    }
  }, [sessionId, sessionPreview, toast]);

  useEffect(() => {
    setPanelAction(null);
    setCancelReason("");
    setNotePatientId(null);
    setNoteDraft("");
    load();
  }, [load]);

  const closePanelAction = () => {
    setPanelAction(null);
    setCancelReason("");
    if (journal) {
      const branchId = journal.branch_id || journal.branch?.id || "";
      const doctorId = journal.doctor_id || journal.trainer?.id || "";
      setRescheduleForm({
        ...sessionScheduleFromIso(journal.start_datetime, journal.end_datetime),
        branch_id: branchId,
      });
      setDetailsForm({ branch_id: branchId, doctor_id: doctorId });
      setCompetitionForm(competitionFormFromSession(journal));
    }
  };

  useEffect(() => {
    onPanelActionChange?.(panelAction);
  }, [panelAction, onPanelActionChange]);

  useEffect(() => {
    if (!sheetBackRef) return undefined;
    sheetBackRef.current = () => {
      if (panelAction) {
        closePanelAction();
        return true;
      }
      return false;
    };
    return () => {
      sheetBackRef.current = () => false;
    };
  }, [panelAction, sheetBackRef, journal]);

  const saveSessionNote = async (patientId) => {
    setNoteSaving(true);
    try {
      await clinicAPI.createClientNote(patientId, {
        body: noteDraft.trim(),
        session_id: sessionId,
      });
      toast.success(noteDraft.trim() ? "Заметка сохранена" : "Заметка убрана");
      setNotePatientId(null);
      setNoteDraft("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось сохранить заметку");
    } finally {
      setNoteSaving(false);
    }
  };

  const mark = async (patientId, status, attendanceRow, absence_reason) => {
    if (!canMarkSessionAttendance(journal)) {
      toast.error("Занятие отменено");
      return;
    }
    const payload = attendanceRow
      ? attendanceMarkPayload(attendanceRow, status)
      : { status };
    setMarkingId(patientId);
    try {
      const data = await clinicAPI.markSessionAttendance(sessionId, {
        patient_id: patientId,
        ...payload,
        absence_reason: payload.clear ? undefined : absence_reason,
      });
      if (payload.clear) toast.success("Отметка снята");
      else if (status === "present" && data.attendance?.charge_error) {
        toast.info(data.attendance.charge_error);
      } else if (data.auto_completed) {
        toast.success("Перекличка завершена");
      }
      await load();
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setMarkingId(null);
    }
  };

  const markAllPresent = async () => {
    const pending = (journal?.attendances || []).filter(
      (a) => !attendanceMarkState(a).isPresent,
    );
    if (!pending.length) return;
    setMarkingId("all");
    try {
      const results = await Promise.allSettled(
        pending.map((a) =>
          clinicAPI.markSessionAttendance(sessionId, {
            patient_id: a.patient_id,
            status: "present",
          }),
        ),
      );
      const ok = results.filter((r) => r.status === "fulfilled");
      const fail = results.filter((r) => r.status === "rejected");
      if (fail.length) {
        await load();
        if (ok.length) {
          toast.error(
            `Отмечено ${ok.length} из ${pending.length}. Повторите для остальных.`,
          );
        } else {
          const err = fail[0].reason;
          toast.error(err?.response?.data?.error || "Ошибка");
        }
        return;
      }
      const autoCompleted = ok.some((r) => r.value?.auto_completed);
      await load();
      onUpdated?.();
      toast.success(autoCompleted ? "Перекличка завершена" : "Все отмечены");
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
      load();
    } finally {
      setMarkingId(null);
    }
  };

  const saveReschedule = async (e) => {
    e.preventDefault();
    setActionBusy(true);
    try {
      const { start, end } = sessionScheduleToIso(rescheduleForm);
      const check = canRescheduleToTimes(journal, start);
      if (!check.ok) {
        toast.error(
          rescheduleBlockedMessage(check.reason) || "Нельзя перенести",
        );
        return;
      }
      await clinicAPI.updateTrainingSession(sessionId, {
        start_datetime: start,
        end_datetime: end,
        branch_id: rescheduleForm.branch_id || null,
      });
      toast.success("Занятие перенесено");
      setPanelAction(null);
      await load();
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось перенести");
    } finally {
      setActionBusy(false);
    }
  };

  const saveDetails = async (e) => {
    e?.preventDefault?.();
    if (!detailsForm?.doctor_id) {
      toast.error(`Выберите ${L.trainer.toLowerCase()}`);
      return;
    }
    setActionBusy(true);
    try {
      await clinicAPI.updateTrainingSession(sessionId, {
        branch_id: detailsForm.branch_id || null,
        doctor_id: detailsForm.doctor_id,
      });
      toast.success("Данные обновлены");
      setPanelAction(null);
      await load();
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось сохранить");
    } finally {
      setActionBusy(false);
    }
  };

  const saveCompetition = async (e) => {
    e?.preventDefault?.();
    if (!competitionForm) return;
    setActionBusy(true);
    try {
      await clinicAPI.updateTrainingSession(
        sessionId,
        competitionPayloadFromForm(competitionForm),
      );
      toast.success("Данные соревнования сохранены");
      setPanelAction(null);
      await load();
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось сохранить");
    } finally {
      setActionBusy(false);
    }
  };

  const uploadCompetitionCover = async (file) => {
    setCoverUploading(true);
    try {
      const res = await clinicAPI.uploadSessionCover(sessionId, file);
      setCompetitionForm((prev) => ({
        ...prev,
        cover_image_url: res.cover_image_url,
      }));
      toast.success("Обложка обновлена");
      await load();
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось загрузить обложку");
    } finally {
      setCoverUploading(false);
    }
  };

  const cancelSession = async () => {
    setActionBusy(true);
    try {
      await clinicAPI.cancelTrainingSession(sessionId, {
        reason: cancelReason.trim() || undefined,
      });
      toast.success("Занятие отменено");
      setPanelAction(null);
      onUpdated?.();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка отмены");
    } finally {
      setActionBusy(false);
    }
  };

  const sess = sessionId ? journal || sessionPreview : null;
  const isEvent = sess?.session_kind === "event";
  const isCompetition = isEvent && sess?.event_kind === "competition";
  const isIndividual = isIndividualSession(sess);
  const attendances = journal?.attendances || [];
  const roll = rollCallStatsFromAttendances(attendances, journal || sess);
  const present = roll.present;
  const markedCount = roll.marked;
  const absentCount = attendances.filter(
    (a) => attendanceMarkState(a).isAbsent,
  ).length;
  const unmarkedCount = Math.max(
    0,
    (attendances.length || sess?.expected_count || 0) - markedCount,
  );
  const expected = attendances.length || sess?.expected_count || 0;
  const pct = expected ? Math.min(100, Math.round((present / expected) * 100)) : 0;
  const groupIdx = groups.findIndex((g) => g.id === sess?.group?.id);
  const color = sessionColor(sess, groupIdx);
  const isCancelled = journal?.status === "cancelled";
  const isCompleted = journal?.status === "completed";
  const canMarkAttendance =
    Boolean(journal) && !isCancelled && canMarkSessionAttendance(journal);
  const canEditSchedule =
    Boolean(journal) &&
    !isCancelled &&
    !isCompleted &&
    canDragSession(journal);

  const pill = sessionDisplayStatus(journal || sess);

  const journalLink = journal
    ? buildJournalUrl({
        date: journal.start_datetime
          ? toDateInputValue(new Date(journal.start_datetime))
          : undefined,
        sessionId: journal.id,
        groupId: journal.group?.id,
      })
    : null;

  const openJournal = (e) => {
    if (!journalLink) return;
    e.preventDefault();
    if (isSheet) onDismiss?.();
    navigate(journalLink);
  };

  const openTrainerProfile = (e) => {
    if (!trainerProfileTo) return;
    e.preventDefault();
    if (isSheet) onDismiss?.();
    navigate(trainerProfileTo);
  };

  const dockWrapClass = isDialog ? aa.calSessionDialogDock : aa.calSlotDock;
  const dockPrimaryClass = isDialog
    ? aa.calSessionDialogDockPrimary
    : aa.calSlotDockPrimary;
  const dockRowClass = isDialog ? aa.calSessionDialogDockRow : aa.calSlotDockRow;
  const dockBtnClass = isDialog ? aa.calSessionDialogDockBtn : aa.calSlotDockBtn;
  const dockBtnDangerClass = isDialog
    ? aa.calSessionDialogDockBtnDanger
    : aa.calSlotDockBtnDanger;
  const dockFormBtnsClass = isDialog
    ? aa.calSessionDialogDockFormBtns
    : `${aa.calSlotDockFormBtns} ${aa.calSlotDockFormBtnsStack}`;
  const dockGhostClass = isDialog
    ? aa.calSessionDialogDockGhost
    : aa.calSlotDockGhost;
  const dockDangerClass = isDialog
    ? aa.calSessionDialogDockDanger
    : aa.calSlotDockDanger;
  const dockSubmitClass = isDialog
    ? aa.calSessionDialogDockSubmit
    : aa.calSlotDockPrimary;

  const branchName = resolveBranchName(sess, branches);
  const branchAddress = resolveBranchAddress(sess, branches);
  const trainerName = resolveTrainerName(sess, doctors);
  const trainerProfileId =
    sess?.doctor_id || sess?.trainer?.id || sess?.doctor?.id || null;
  const trainerProfileTo = adminTrainerProfilePath(trainerProfileId);
  const isSelfTrainer = isOwnTrainerRecord(trainerProfileId, myTrainerId);
  const showTrainerProfileLink =
    Boolean(trainerProfileTo && trainerName && !isSelfTrainer);

  const trainerFactNode = showTrainerProfileLink ? (
    <Link
      to={trainerProfileTo}
      className={isSheet ? aa.calSlotFactLink : s.calSlotTrainerLink}
      onClick={openTrainerProfile}
    >
      {trainerName}
    </Link>
  ) : (
    trainerName || "—"
  );

  const panelClass = isSheet
    ? `${s.calDayPanel} ${s.calDayPanelSheet} ${
        isDialog ? aa.calSlotDialogPanel : aa.calSlotSheetPanel
      }`
    : s.calDayPanel;

  const sheetActionFoot = (primaryLabel, { formId, onPrimary, danger } = {}) => (
    <div className={dockFormBtnsClass}>
      <button
        type="button"
        className={dockGhostClass}
        disabled={actionBusy}
        onClick={closePanelAction}
      >
        Отмена
      </button>
      {danger ? (
        <button
          type="button"
          className={dockDangerClass}
          disabled={actionBusy}
          onClick={onPrimary}
        >
          {actionBusy ? "…" : primaryLabel}
        </button>
      ) : (
        <button
          type="submit"
          form={formId}
          className={dockSubmitClass}
          disabled={actionBusy}
        >
          {actionBusy ? "…" : primaryLabel}
        </button>
      )}
    </div>
  );

  const sheetActionPanel =
    panelAction === "competition" && competitionForm ? (
      <div className={aa.calSlotActionPanel}>
        <h4 className={aa.calSlotActionTitle}>Соревнование</h4>
        <form
          id="cal-slot-competition-form"
          className={aa.calSlotActionForm}
          onSubmit={saveCompetition}
        >
          <CompetitionDetailsFields
            form={competitionForm}
            onChange={(patch) =>
              setCompetitionForm({ ...competitionForm, ...patch })
            }
            sessionId={sessionId}
            onCoverUpload={uploadCompetitionCover}
            coverUploading={coverUploading}
            sheet
          />
        </form>
      </div>
    ) : panelAction === "details" && detailsForm && !isCompetition ? (
      <div className={aa.calSlotActionPanel}>
        <h4 className={aa.calSlotActionTitle}>Зал и {L.trainer.toLowerCase()}</h4>
        <form
          id="cal-slot-details-form"
          className={aa.calSlotActionForm}
          onSubmit={saveDetails}
        >
          <Field label="Зал">
            <Select
              value={detailsForm.branch_id}
              onChange={(e) =>
                setDetailsForm({ ...detailsForm, branch_id: e.target.value })
              }
            >
              <option value="">Не указан</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={L.trainer}>
            <Select
              value={detailsForm.doctor_id}
              onChange={(e) =>
                setDetailsForm({ ...detailsForm, doctor_id: e.target.value })
              }
              required
            >
              <option value="">—</option>
              {doctors.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.user?.full_name || d.full_name}
                </option>
              ))}
            </Select>
          </Field>
        </form>
      </div>
    ) : panelAction === "reschedule" && rescheduleForm ? (
      <div className={aa.calSlotActionPanel}>
        <h4 className={aa.calSlotActionTitle}>Перенос занятия</h4>
        <p className={aa.calSlotActionHint}>
          Меняется только это занятие. Шаблон расписания группы не затрагивается.
        </p>
        <form
          id="cal-slot-reschedule-form"
          className={aa.calSlotActionForm}
          onSubmit={saveReschedule}
        >
          <SessionDateTimeFields
            sheet
            date={rescheduleForm.date}
            startTime={rescheduleForm.startTime}
            endTime={rescheduleForm.endTime}
            onChange={(patch) =>
              setRescheduleForm({ ...rescheduleForm, ...patch })
            }
          />
        </form>
      </div>
    ) : panelAction === "cancel" ? (
      <div className={aa.calSlotActionPanel}>
        <h4 className={aa.calSlotActionTitle}>Отмена занятия</h4>
        <p className={aa.calSlotActionHint}>
          Занятие исчезнет из расписания. Причину можно указать ниже.
        </p>
        <div className={aa.calSlotActionForm}>
          <Field label="Причина (необязательно)">
            <Input
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Праздник, болезнь…"
            />
          </Field>
        </div>
      </div>
    ) : null;

  const sheetDock = useMemo(() => {
    if (!isSheet || !sess) return null;
    const DockTag = isDialog ? "div" : "footer";
    return (
      <DockTag className={dockWrapClass}>
        {panelAction === "competition" && competitionForm
          ? sheetActionFoot("Сохранить", { formId: "cal-slot-competition-form" })
          : panelAction === "details" && detailsForm && !isCompetition
          ? sheetActionFoot("Сохранить", { formId: "cal-slot-details-form" })
          : panelAction === "reschedule" && rescheduleForm
            ? sheetActionFoot("Перенести", { formId: "cal-slot-reschedule-form" })
            : panelAction === "cancel"
              ? sheetActionFoot("Отменить занятие", {
                  danger: true,
                  onPrimary: cancelSession,
                })
              : null}
        {!panelAction ? (
          <>
            {journalLink ? (
              <Link
                to={journalLink}
                className={dockPrimaryClass}
                onClick={openJournal}
              >
                {L.openJournal}
              </Link>
            ) : null}
            {canEditSchedule ? (
              <div className={dockRowClass}>
                <button
                  type="button"
                  className={dockBtnClass}
                  disabled={actionBusy}
                  onClick={() =>
                    setPanelAction(isCompetition ? "competition" : "details")
                  }
                >
                  {isCompetition ? "Инфо" : "Данные"}
                </button>
                <button
                  type="button"
                  className={dockBtnClass}
                  disabled={actionBusy}
                  onClick={() => setPanelAction("reschedule")}
                >
                  Перенести
                </button>
                <button
                  type="button"
                  className={dockBtnDangerClass}
                  disabled={actionBusy}
                  onClick={() => setPanelAction("cancel")}
                >
                  Отменить
                </button>
              </div>
            ) : null}
          </>
        ) : null}
      </DockTag>
    );
  }, [
    isSheet,
    isDialog,
    dockWrapClass,
    panelAction,
    competitionForm,
    detailsForm,
    isCompetition,
    rescheduleForm,
    journalLink,
    dockPrimaryClass,
    canEditSchedule,
    dockRowClass,
    dockBtnClass,
    actionBusy,
    dockBtnDangerClass,
    dockFormBtnsClass,
    dockGhostClass,
    dockDangerClass,
    dockSubmitClass,
    sess,
  ]);

  useLayoutEffect(() => {
    if (!isDialog || !onDockChange) return undefined;
    onDockChange(sheetDock);
    return () => onDockChange(null);
  }, [isDialog, onDockChange, sheetDock]);

  if (!sessionId) {
    return (
      <div className={s.calDayPanelEmpty} aria-live="polite">
        <div className={s.calDayPanelEmptyVisual} aria-hidden>
          <span className={s.calDayPanelEmptyIcon}>📅</span>
          <span className={s.calDayPanelEmptyArrow}>→</span>
          <span className={s.calDayPanelEmptySlot}>09:00</span>
        </div>
        <h3 className={s.calDayPanelEmptyTitle}>Выберите занятие</h3>
        <p className={s.calDayPanelEmptyHint}>
          Нажмите на карточку в сетке расписания слева — здесь откроется
          перекличка, заметки и действия с группой.
        </p>
        <ol className={s.calDayPanelEmptySteps}>
          <li>
            <span className={s.calDayPanelEmptyStepNum}>1</span>
            Найдите нужное время
          </li>
          <li>
            <span className={s.calDayPanelEmptyStepNum}>2</span>
            Нажмите на занятие
          </li>
          <li>
            <span className={s.calDayPanelEmptyStepNum}>3</span>
            Отметьте посещаемость
          </li>
        </ol>
      </div>
    );
  }

  if (!sess) {
    return (
      <div className={s.calDayPanel}>
        <div className={s.calDayPanelEmptyState}>
          {loading ? (
            <>
              <span className={s.calSlotLoadingDot} />
              <span>Загрузка занятия…</span>
            </>
          ) : (
            <>
              <span className={s.calDayPanelEmptyIcon} aria-hidden>
                ⚠
              </span>
              <strong>Занятие не найдено</strong>
              <p>Выберите другое в расписании</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
    <div className={panelClass}>
      {loading && !journal ? (
        <div className={s.calSlotLoading}>
          <span className={s.calSlotLoadingDot} />
          Загрузка…
        </div>
      ) : (
        <>
          {isSheet ? (
            <header
              className={aa.calSlotCard}
              style={{ "--slot-color": color }}
            >
              <div className={aa.calSlotCardAccent} aria-hidden />
              <div className={aa.calSlotCardInner}>
                <div className={aa.calSlotCardTop}>
                  <span className={aa.calSlotCardTag}>
                    {isEvent
                      ? sess?.event_kind === "competition"
                        ? "Соревнование"
                        : "Событие"
                      : isIndividual
                        ? "Индивидуальная"
                        : sess?.group?.sport_direction || "Тренировка"}
                  </span>
                  <span
                    className={`${aa.calSlotStatusPill} ${aa[`calSlotStatusPill_${pill.tone}`]}`}
                  >
                    {pill.label}
                  </span>
                </div>
                <h3 className={aa.calSlotCardTitle}>{sessionLabel(sess)}</h3>
                <p className={aa.calSlotCardWhen}>
                  {formatTimeRu(sess?.start_datetime)}
                  <span aria-hidden> – </span>
                  {formatTimeRu(sess?.end_datetime)}
                  {sess?.start_datetime ? (
                    <>
                      <span className={aa.calSlotCardWhenDot}>·</span>
                      {formatDateRu(sess.start_datetime, {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                      })}
                    </>
                  ) : null}
                </p>
                {panelAction ? (
                  <p className={aa.calSlotCardMetaLine}>
                    {isCompetition
                      ? competitionVenueLine(sess) || "Площадка не указана"
                      : `${branchName || "Зал не указан"}${trainerName ? ` · ${trainerName}` : ""}`}
                  </p>
                ) : isCompetition ? (
                  <ul className={aa.calSlotFacts}>
                    <li>
                      <span className={aa.calSlotFactLabel}>Площадка</span>
                      <span className={aa.calSlotFactValue}>
                        {competitionVenueLine(sess) || "Не указана"}
                      </span>
                    </li>
                    {competitionAssemblyLabel(sess) ? (
                      <li>
                        <span className={aa.calSlotFactLabel}>Сбор</span>
                        <span className={aa.calSlotFactValue}>
                          {competitionAssemblyLabel(sess)}
                        </span>
                      </li>
                    ) : null}
                    {sess?.contact_info ? (
                      <li>
                        <span className={aa.calSlotFactLabel}>Контакт</span>
                        <span className={aa.calSlotFactValue}>
                          {sess.contact_info}
                        </span>
                      </li>
                    ) : null}
                  </ul>
                ) : (
                  <ul className={aa.calSlotFacts}>
                    <li>
                      <span className={aa.calSlotFactLabel}>Зал</span>
                      <span
                        className={
                          branchAddress
                            ? aa.calSlotFactValueStack
                            : aa.calSlotFactValue
                        }
                      >
                        <span>{branchName || "Не указан"}</span>
                        {branchAddress ? (
                          <span className={aa.calSlotFactValueSub}>
                            {branchAddress}
                          </span>
                        ) : null}
                      </span>
                    </li>
                    <li>
                      <span className={aa.calSlotFactLabel}>{L.trainer}</span>
                      <span className={aa.calSlotFactValue}>{trainerFactNode}</span>
                    </li>
                  </ul>
                )}
              </div>
            </header>
          ) : (
            <header
              className={s.calSlotHero}
              style={{ "--slot-color": color }}
            >
              <div className={s.calSlotHeroAccent} aria-hidden />
              <div className={s.calSlotHeroInner}>
                <div className={s.calSlotHeroRow}>
                  <span className={s.calSlotBadge}>
                    {isEvent
                      ? sess?.event_kind === "competition"
                        ? "Соревнование"
                        : "Событие"
                      : isIndividual
                        ? "Индивидуальная"
                        : sess?.group?.sport_direction || "Тренировка"}
                  </span>
                  <span
                    className={`${s.calSlotStatus} ${s[`calSlotStatus_${pill.tone}`]}`}
                  >
                    <span className={s.calSlotStatusDot} aria-hidden />
                    {pill.label}
                  </span>
                </div>
                <h3 className={s.calSlotTitle}>{sessionLabel(sess)}</h3>
                <p className={s.calSlotTime}>
                  {formatTimeRu(sess?.start_datetime)}
                  <span className={s.calSlotTimeSep}>–</span>
                  {formatTimeRu(sess?.end_datetime)}
                </p>
                {sess?.start_datetime ? (
                  <p className={s.calSlotDate}>
                    {formatDateRu(sess.start_datetime, { weekday: "long" })}
                  </p>
                ) : null}
                <p className={s.calSlotMeta}>
                  {branchName || "Зал не указан"}
                  {trainerName ? (
                    <>
                      {" · "}
                      {showTrainerProfileLink ? (
                        <Link
                          to={trainerProfileTo}
                          className={s.calSlotTrainerLink}
                          onClick={openTrainerProfile}
                        >
                          {trainerName}
                        </Link>
                      ) : (
                        trainerName
                      )}
                    </>
                  ) : null}
                </p>
              </div>
            </header>
          )}

          <div
            className={
              isSheet ? aa.calSlotSheetScroll : s.calDayPanelBody
            }
          >
            {!isSheet &&
            canEditSchedule &&
            !panelAction &&
            !isCancelled &&
            !isCompleted ? (
              <div className={s.calSlotToolbar}>
                <button
                  type="button"
                  className={s.calSlotToolbarBtn}
                  disabled={actionBusy}
                  onClick={() =>
                    setPanelAction(isCompetition ? "competition" : "details")
                  }
                >
                  {isCompetition ? "Инфо о соревновании" : "Изменить данные"}
                </button>
                <button
                  type="button"
                  className={s.calSlotToolbarBtn}
                  disabled={actionBusy}
                  onClick={() => setPanelAction("reschedule")}
                >
                  Перенести
                </button>
                <button
                  type="button"
                  className={`${s.calSlotToolbarBtn} ${s.calSlotToolbarBtnMuted}`}
                  disabled={actionBusy}
                  onClick={() => setPanelAction("cancel")}
                >
                  Отменить
                </button>
              </div>
            ) : null}

            {!isSheet && panelAction === "competition" && competitionForm ? (
              <form
                onSubmit={saveCompetition}
                className={`${s.calEditForm} ${s.calSlotForm}`}
              >
                <CompetitionDetailsFields
                  form={competitionForm}
                  onChange={(patch) =>
                    setCompetitionForm({ ...competitionForm, ...patch })
                  }
                  sessionId={sessionId}
                  onCoverUpload={uploadCompetitionCover}
                  coverUploading={coverUploading}
                />
                <div className={s.calSlotFormBtns}>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={actionBusy}
                    onClick={closePanelAction}
                  >
                    Назад
                  </Button>
                  <Button type="submit" size="sm" disabled={actionBusy}>
                    {actionBusy ? "…" : "Сохранить"}
                  </Button>
                </div>
              </form>
            ) : null}

            {!isSheet && panelAction === "details" && detailsForm && !isCompetition ? (
              <form
                onSubmit={saveDetails}
                className={`${s.calEditForm} ${s.calSlotForm}`}
              >
                <Field label="Зал">
                  <Select
                    value={detailsForm.branch_id}
                    onChange={(e) =>
                      setDetailsForm({
                        ...detailsForm,
                        branch_id: e.target.value,
                      })
                    }
                  >
                    <option value="">Не указан</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label={L.trainer}>
                  <Select
                    value={detailsForm.doctor_id}
                    onChange={(e) =>
                      setDetailsForm({
                        ...detailsForm,
                        doctor_id: e.target.value,
                      })
                    }
                    required
                  >
                    <option value="">—</option>
                    {doctors.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.user?.full_name || d.full_name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <div className={s.calSlotFormBtns}>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={actionBusy}
                    onClick={closePanelAction}
                  >
                    Назад
                  </Button>
                  <Button type="submit" size="sm" disabled={actionBusy}>
                    {actionBusy ? "…" : "Сохранить"}
                  </Button>
                </div>
              </form>
            ) : null}

            {!isSheet && panelAction === "reschedule" && rescheduleForm ? (
              <form
                onSubmit={saveReschedule}
                className={`${s.calEditForm} ${s.calSlotForm}`}
              >
                <SessionDateTimeFields
                  date={rescheduleForm.date}
                  startTime={rescheduleForm.startTime}
                  endTime={rescheduleForm.endTime}
                  onChange={(patch) =>
                    setRescheduleForm({ ...rescheduleForm, ...patch })
                  }
                />
                <div className={s.calSlotFormBtns}>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={actionBusy}
                    onClick={closePanelAction}
                  >
                    Назад
                  </Button>
                  <Button type="submit" size="sm" disabled={actionBusy}>
                    {actionBusy ? "…" : "Перенести"}
                  </Button>
                </div>
              </form>
            ) : null}

            {!isSheet && panelAction === "cancel" ? (
              <div className={s.calSlotForm}>
                <Field label="Причина (необязательно)">
                  <Input
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Праздник, болезнь…"
                  />
                </Field>
                <div className={s.calSlotFormBtns}>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={actionBusy}
                    onClick={closePanelAction}
                  >
                    Назад
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="danger"
                    disabled={actionBusy}
                    onClick={cancelSession}
                  >
                    {actionBusy ? "…" : "Отменить занятие"}
                  </Button>
                </div>
              </div>
            ) : null}

            {!isSheet && !isEvent && !isCancelled && expected > 0 ? (
              <div className={s.calSlotAttendSummary}>
                <div className={s.calSlotAttendHead}>
                  <span className={s.calSlotAttendLabel}>
                    Отмечено {markedCount}/{expected}
                    {present > 0 ? ` · было ${present}` : ""}
                    {absentCount > 0 ? ` · не было ${absentCount}` : ""}
                    {unmarkedCount > 0 ? ` · ждут ${unmarkedCount}` : ""}
                  </span>
                  <span className={s.calSlotAttendPct}>{pct}%</span>
                </div>
                <div className={s.calSlotAttendTrack}>
                  <div
                    className={s.calSlotAttendFill}
                    style={{
                      width: `${pct}%`,
                      background: color,
                    }}
                  />
                </div>
              </div>
            ) : null}

            {isSheet && sheetActionPanel ? sheetActionPanel : null}

            {isSheet && !panelAction && !isEvent && !isCancelled ? (
              <div className={aa.calSlotSheetInfo}>
                {expected > 0 || journal?.expectation_summary?.total ? (
                  <SessionSheetAttendanceCard
                    session={journal}
                    expectationSummary={journal?.expectation_summary}
                    pollStats={journal?.poll?.stats}
                    roll={roll}
                    accentColor={color}
                    sheetOnly
                  />
                ) : (
                  <p className={aa.calSlotSheetNote}>
                    {isIndividual
                      ? "Клиент не указан — проверьте карточку занятия."
                      : "В группе пока нет участников — добавьте их в карточке группы."}
                  </p>
                )}
                <p className={aa.calSlotSheetHint}>
                  Журнал занятия — отметки, заметки и список спортсменов.
                </p>
              </div>
            ) : null}

            {!isSheet && !isEvent && !isCancelled ? (
              <div className={s.calSlotMembers}>
                <div className={s.calSlotMembersHead}>
                  <h4>Участники</h4>
                  {canMarkAttendance && present < expected ? (
                    <button
                      type="button"
                      className={s.calSlotMarkAll}
                      onClick={markAllPresent}
                      disabled={markingId === "all" || !journal}
                    >
                      {markingId === "all" ? "…" : "Отметить всех"}
                    </button>
                  ) : null}
                </div>
                {attendances.length === 0 ? (
                  <p className={s.calSlotMembersEmpty}>
                    {isIndividual
                      ? "Клиент не добавлен в занятие"
                      : "В группе пока никого нет — добавьте участников в карточке группы"}
                  </p>
                ) : (
                  <ul className={s.calSlotMemberList}>
                    {attendances.map((a) => {
                      const { isPresent, isAbsent, isAbsentOnly } =
                        attendanceMarkState(a);
                      const busy = markingId === a.patient_id;
                      const hasNote = Boolean(a.session_note?.body?.trim());
                      const noteOpen = notePatientId === a.patient_id;
                      return (
                        <li key={a.patient_id} className={s.calSlotMember}>
                          <div className={s.calSlotMemberRow}>
                            <div
                              className={s.calSlotMemberAvatar}
                              style={{ "--av-color": color }}
                              aria-hidden
                            >
                              {memberInitials(a.patient?.full_name)}
                            </div>
                            <div className={s.calSlotMemberInfo}>
                              <div className={s.calSlotMemberNameRow}>
                                <Link to={`/manage/app/clients/${a.patient_id}`}>
                                  {a.patient?.full_name || "—"}
                                </Link>
                                <button
                                  type="button"
                                  className={`${s.calSlotMemberNoteBtn} ${hasNote ? s.calSlotMemberNoteBtnActive : ""}`}
                                  title="Заметка"
                                  onClick={() => {
                                    if (noteOpen) {
                                      setNotePatientId(null);
                                      setNoteDraft("");
                                    } else {
                                      setNotePatientId(a.patient_id);
                                      setNoteDraft(a.session_note?.body || "");
                                    }
                                  }}
                                >
                                  <span aria-hidden>✎</span>
                                </button>
                              </div>
                              {a.membership_warning === "unpaid" ? (
                                <span
                                  className={`${s.calSlotMemberWarn} ${s.calSlotMemberWarn_unpaid}`}
                                >
                                  Должник · не оплачен
                                </span>
                              ) : null}
                              {(a.membership_warning === "none" ||
                                a.membership_warning === "expired") && (
                                <span className={s.calSlotMemberWarn}>
                                  {a.membership_warning === "none"
                                    ? "Нет абонемента"
                                    : "Абонемент истёк"}
                                </span>
                              )}
                            </div>
                            <div
                              className={s.calSlotAttendToggle}
                              role="group"
                              aria-label="Посещаемость"
                            >
                              <button
                                type="button"
                                className={`${s.calSlotAttendToggleBtn} ${isPresent ? s.calSlotAttendToggleBtn_on : ""}`}
                                disabled={
                                  !canMarkAttendance || busy || markingId === "all"
                                }
                                onClick={() => mark(a.patient_id, "present", a)}
                                aria-pressed={isPresent}
                              >
                                ✓
                              </button>
                              <button
                                type="button"
                                className={`${s.calSlotAttendToggleBtn} ${isAbsent ? s.calSlotAttendToggleBtn_off : ""}`}
                                disabled={
                                  !canMarkAttendance || busy || markingId === "all"
                                }
                                onClick={() => {
                                  if (isAbsentOnly) {
                                    mark(a.patient_id, "absent", a);
                                    return;
                                  }
                                  setAbsencePick({
                                    patientId: a.patient_id,
                                    name: a.patient?.full_name,
                                  });
                                }}
                                aria-pressed={isAbsent}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                          {noteOpen ? (
                            <div className={s.calSlotNoteField}>
                              <Input
                                value={noteDraft}
                                onChange={(e) => setNoteDraft(e.target.value)}
                                placeholder="Заметка к занятию…"
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    saveSessionNote(a.patient_id);
                                  }
                                }}
                              />
                              <div className={s.calSlotNoteActions}>
                                <button
                                  type="button"
                                  className={s.calSlotNoteBtnGhost}
                                  disabled={noteSaving}
                                  onClick={() => {
                                    setNotePatientId(null);
                                    setNoteDraft("");
                                  }}
                                >
                                  Отмена
                                </button>
                                <button
                                  type="button"
                                  className={s.calSlotNoteBtnSave}
                                  disabled={noteSaving}
                                  onClick={() => saveSessionNote(a.patient_id)}
                                >
                                  {noteSaving ? "…" : "Сохранить"}
                                </button>
                              </div>
                            </div>
                          ) : hasNote && !noteOpen ? (
                            <p className={s.calSlotNotePreview}>{a.session_note.body}</p>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ) : isEvent && !isCancelled ? (
              sess?.event_kind === "competition" ? (
                !(isSheet && panelAction === "competition") ? (
                  <CompetitionRegistrationsPanel
                    sessionId={sessionId}
                    variant={isSheet ? "sheet" : "default"}
                  />
                ) : null
              ) : (
                <p className={s.calSlotEventNote}>
                  {journal?.notes || sess?.notes || "Разовое событие без переклички."}
                </p>
              )
            ) : null}

            {isCancelled && journal?.cancellation_reason ? (
              <p className={s.calSlotEventNote}>
                Причина отмены: {journal.cancellation_reason}
              </p>
            ) : null}
          </div>

          {sheetDock && variant === "sheet" ? sheetDock : null}

          {!isSheet && journalLink && !panelAction ? (
            <footer className={s.calDayPanelFooter}>
              <Link to={journalLink} className={s.calSlotJournalLink} onClick={openJournal}>
                {L.openJournal}
              </Link>
            </footer>
          ) : null}
        </>
      )}
    </div>
    <AttendanceAbsenceSheet
      open={Boolean(absencePick)}
      onClose={() => setAbsencePick(null)}
      subtitle={absencePick?.name}
      onPick={(reason) => {
        const row = (journal?.attendances || []).find(
          (x) => x.patient_id === absencePick?.patientId,
        );
        mark(absencePick.patientId, "absent", row, reason);
      }}
    />
  </>
  );
}
